import React, { useState } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { Menu, X, Camera } from "lucide-react";
import { StyleGlyph, HomeGlyph } from "@/components/WorldGlyphs";
import { motion, AnimatePresence } from "framer-motion";
import MoireLogo from "@/components/MoireLogo";
import SnapButton from "@/components/SnapButton";
import { useAuth } from "@/context/AuthContext";
import { useReduceMotion, setReduceMotion } from "@/lib/motion";

const NAV = [
  { to: "/match", label: "Match My Look" },
  { to: "/dont-mess-up", label: "Don't Mess Up" },
  { to: "/saved", label: "Saved Looks" },
  { to: "/wardrobe", label: "My Wardrobe" },
  { to: "/style-me", label: "Style Me" },
  { to: "/outfit-check", label: "Outfit Check" },
];

const HOME_NAV = [
  { to: "/home", label: "MOIRÉ Home" },
  { to: "/home/style-piece", label: "Style A Piece" },
  { to: "/home/room", label: "Check My Room" },
];

function WorldSwitch({ isHome, onNavigate }) {
  return (
    <div className="inline-flex items-center border border-[#F5F2EA]/15 rounded-full p-0.5" data-testid="world-switch">
      <button onClick={() => onNavigate("/")} data-testid="world-style"
        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full font-sans text-[10px] tracking-luxe uppercase transition-colors ${!isHome ? "bg-[#C8A97E] text-[#0D0D0D]" : "text-[#8A8170] hover:text-[#F5F2EA]"}`}>
        <StyleGlyph size={14} /> Style</button>
      <button onClick={() => onNavigate("/home")} data-testid="world-home"
        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full font-sans text-[10px] tracking-luxe uppercase transition-colors ${isHome ? "bg-[#C8A97E] text-[#0D0D0D]" : "text-[#8A8170] hover:text-[#F5F2EA]"}`}>
        <HomeGlyph size={14} /> Home</button>
    </div>
  );
}

export default function Layout({ children }) {
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const loc = useLocation();
  const isHome = loc.pathname.startsWith("/home");
  const homeNav = user ? [...HOME_NAV, { to: "/home/my-home", label: "My Home" }] : HOME_NAV;
  const nav = isHome ? homeNav : NAV;
  const go = (to) => { navigate(to); setOpen(false); };

  return (
    <div className="grain min-h-screen flex flex-col bg-[#0D0D0D] relative">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#0D0D0D]/70 border-b border-[#F5F2EA]/10">
        <div className="max-w-7xl mx-auto px-5 md:px-8 h-16 md:h-20 flex items-center justify-between gap-4">
          <Link to={isHome ? "/home" : "/"} data-testid="nav-home"><MoireLogo compact /></Link>

          <nav className="hidden lg:flex items-center gap-8">
            {nav.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.to === "/home"} data-testid={`nav-${n.to.slice(1).replace(/\//g, "-")}`}
                className={({ isActive }) =>
                  `font-sans text-xs tracking-wider uppercase link-underline transition-colors ${
                    isActive ? "text-[#C8A97E]" : "text-[#F5F2EA]/70 hover:text-[#F5F2EA]"}`}>
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-5">
            <WorldSwitch isHome={isHome} onNavigate={navigate} />
            {user ? (
              <div className="flex items-center gap-4">
                <Link to="/profile" data-testid="nav-profile"
                  className="font-sans text-xs tracking-wider uppercase text-[#F5F2EA]/70 hover:text-[#C8A97E]">
                  {user.name || "Profile"}
                </Link>
                <button onClick={() => { logout(); navigate("/"); }} data-testid="nav-logout"
                  className="font-sans text-xs tracking-wider uppercase text-[#8A8170] hover:text-[#F5F2EA]">
                  Sign Out
                </button>
              </div>
            ) : (
              <Link to="/signin" data-testid="nav-signin"
                className="font-sans text-xs tracking-wider uppercase text-[#F5F2EA]/70 hover:text-[#C8A97E]">
                Sign In
              </Link>
            )}
            {!isHome && <SnapButton size="sm" testid="nav-snap" />}
          </div>

          <button className="lg:hidden text-[#F5F2EA]" onClick={() => setOpen(true)} data-testid="nav-menu-open">
            <Menu size={24} strokeWidth={1.5} />
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-[#0D0D0D] lg:hidden flex flex-col" data-testid="mobile-menu">
            <div className="h-16 px-5 flex items-center justify-between border-b border-[#F5F2EA]/10">
              <MoireLogo compact />
              <button onClick={() => setOpen(false)} data-testid="nav-menu-close" className="text-[#F5F2EA]">
                <X size={24} strokeWidth={1.5} />
              </button>
            </div>
            <div className="px-8 pt-6"><WorldSwitch isHome={isHome} onNavigate={go} /></div>
            <nav className="flex-1 flex flex-col justify-center gap-2 px-8">
              {nav.map((n) => (
                <Link key={n.to} to={n.to} onClick={() => setOpen(false)} data-testid={`mnav-${n.to.slice(1).replace(/\//g, "-")}`}
                  className="font-serif text-3xl py-2 text-[#F5F2EA] hover:text-[#C8A97E] transition-colors">
                  {n.label}
                </Link>
              ))}
              <div className="gold-line my-6" />
              {user ? (
                <>
                  <Link to="/profile" onClick={() => setOpen(false)} className="font-sans text-sm tracking-wider uppercase text-[#F5F2EA]/70 py-2">Profile</Link>
                  <button onClick={() => { logout(); setOpen(false); navigate("/"); }} className="text-left font-sans text-sm tracking-wider uppercase text-[#8A8170] py-2">Sign Out</button>
                </>
              ) : (
                <Link to="/signin" onClick={() => setOpen(false)} className="font-sans text-sm tracking-wider uppercase text-[#F5F2EA]/70 py-2">Sign In</Link>
              )}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>

      <main className="flex-1 relative z-10">{children}</main>

      {/* Mobile floating snap — hidden on capture/upload flows to avoid tap collisions */}
      {!isHome && !["/match", "/dont-mess-up", "/outfit-check", "/wardrobe"].includes(loc.pathname) && (
        <button onClick={() => navigate("/match")} data-testid="floating-snap"
          className="lg:hidden fixed bottom-5 right-5 z-40 h-14 w-14 rounded-full bg-[#C8A97E] text-[#0D0D0D] flex items-center justify-center shadow-lg shadow-black/50">
          <Camera size={22} strokeWidth={1.75} />
        </button>
      )}

      <Footer isHome={isHome} nav={nav} />
    </div>
  );
}

function Footer({ isHome, nav }) {
  return (
    <footer className="relative z-10 border-t border-[#F5F2EA]/10 mt-24">
      <div className="max-w-7xl mx-auto px-5 md:px-8 py-16 grid md:grid-cols-3 gap-10">
        <div>
          <MoireLogo />
          <p className="font-sans text-xs text-[#8A8170] mt-6 leading-relaxed max-w-xs">
            {isHome ? "Your personal interior styling intelligence. Style the space you already have."
                    : "Your personal styling intelligence. One piece. Your best matches. Zero guesswork."}
          </p>
        </div>
        <div className="flex flex-col gap-2">
          <span className="font-sans text-[10px] tracking-luxe uppercase text-[#8A8170] mb-2">{isHome ? "MOIRÉ Home" : "Explore"}</span>
          {nav.map((n) => (
            <Link key={n.to} to={n.to} className="font-sans text-sm text-[#F5F2EA]/70 hover:text-[#C8A97E] transition-colors w-fit">{n.label}</Link>
          ))}
        </div>
        <div className="flex flex-col gap-2">
          <span className="font-sans text-[10px] tracking-luxe uppercase text-[#8A8170] mb-2">MOIRÉ</span>
          <Link to="/about" className="font-sans text-sm text-[#F5F2EA]/70 hover:text-[#C8A97E] w-fit">About</Link>
          <Link to="/collections/kc-cinem-003-1" data-testid="footer-collections-link" className="font-sans text-sm text-[#F5F2EA]/70 hover:text-[#C8A97E] w-fit">Collections — KC-CINEM 003.1</Link>
          <Link to="/privacy" className="font-sans text-sm text-[#F5F2EA]/70 hover:text-[#C8A97E] w-fit">Privacy</Link>
          <Link to="/terms" className="font-sans text-sm text-[#F5F2EA]/70 hover:text-[#C8A97E] w-fit">Terms</Link>
          <a href="https://kendicollections.shop" target="_blank" rel="noopener noreferrer" data-testid="footer-kendi-link" className="font-sans text-sm text-[#F5F2EA]/70 hover:text-[#C8A97E] w-fit">Kendi Collections</a>
        </div>
      </div>
      <div className="border-t border-[#F5F2EA]/10 py-6 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-6">
        <p className="font-sans text-[10px] tracking-luxe uppercase text-[#8A8170]">
          Powered by Kendi Collections · moire.live
        </p>
        <MotionToggle />
      </div>
    </footer>
  );
}

function MotionToggle() {
  const reduce = useReduceMotion();
  return (
    <button onClick={() => setReduceMotion(!reduce)} data-testid="motion-toggle" aria-pressed={reduce}
      title={reduce ? "Turn animations on" : "Reduce animations"}
      className="inline-flex items-center gap-2 font-sans text-[10px] tracking-luxe uppercase text-[#8A8170] hover:text-[#F5F2EA] transition-colors">
      <span className={`relative h-4 w-7 rounded-full transition-colors ${reduce ? "bg-[#F5F2EA]/20" : "bg-[#C8A97E]"}`}>
        <span className={`absolute top-0.5 h-3 w-3 rounded-full bg-[#0D0D0D] transition-all ${reduce ? "left-0.5" : "left-3.5"}`} />
      </span>
      {reduce ? "Motion off" : "Motion on"}
    </button>
  );
}
